/**
 * @file index.tsx
 *
 * @fileoverview Renders the user avatar image.
 */
import { cva } from "class-variance-authority";
import styles from "./userAvatarImage.module.scss";

const userAvatarImage = cva([styles.userAvatar], {
  variants: {
    size: {
      small: styles.userAvatarSmall,
      medium: styles.userAvatarMedium,
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

export interface UserAvatarImageProps {
  /**
   * Alt text for the user image.
   */
  alt: string;
  /**
   * Title for the user image.
   */
  title?: string;
  /**
   * Size of the image.
   */
  size?: "small" | "medium";
  /**
   * Src of the image.
   */
  src?: string;
}

export const UserAvatarImage = ({
  size = "medium",
  title,
  alt,
  src,
}: UserAvatarImageProps) => {
  const avatarLabel = getAvatarName(title, alt);

  if (src) {
    return (
      <img
        className={userAvatarImage({ size })}
        src={src}
        alt={alt ?? ""}
      />
    );
  }

  return (
    <span
      aria-label={avatarLabel || "User avatar"}
      className={userAvatarImage({ size })}
      role="img"
    >
      {getInitials(avatarLabel)}
    </span>
  );
};

/**
 * A helper function to get the avatar name from the title or alt text.
 */
function getAvatarName(title = "", alt = "") {
  return title.trim() || alt.trim();
}

/**
 * Builds initials using the first two words so existing multi-part names keep
 * the same fallback semantics as the previous avatar URL.
 */
function getInitials(name = "") {
  const parts = name.split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return "?";
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  const [firstName, lastName] = parts;

  return `${firstName[0]}${lastName[0]}`.toUpperCase();
}
